import { z } from 'zod';
import type { UpdateVehicleDto } from '@relay-towing/api';

/**
 * The vehicle on a call or an impound, as the phone's vehicle sheet edits
 * it: VIN, make, model, year, colour, odometer and plate. Nothing is
 * required, since a change may be one field and a tow may leave without a
 * VIN. A year, when given, is four digits; the miles are a number.
 *
 * The VIN is not held to 17 characters: a vehicle built before 1981 has a
 * shorter one, and refusing it would refuse every other change to that
 * vehicle too.
 */
export const vehicleSchema = z.object({
  vin: z.string().trim().toUpperCase(),
  make: z.string().trim(),
  model: z.string().trim(),
  year: z
    .string()
    .trim()
    .regex(/^(\d{4})?$/, 'Enter a four-digit year'),
  color: z.string().trim(),
  odometer: z
    .string()
    .trim()
    .regex(/^[\d,]*$/, 'Enter the miles as a number'),
  licensePlate: z.string().trim().toUpperCase(),
});

export type VehicleValues = z.infer<typeof vehicleSchema>;

/** A vehicle as GET /calls/:id and the impound record carry one. */
export interface VehicleRecord {
  id: string;
  vin?: string | null;
  vehicleName?: string | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleYear?: number | null;
  odometer?: number | null;
  licensePlate?: string | null;
  vehicleAttributes?: { color?: string | null } | null;
}

/** A vehicle's own fields, whether or not the server has a row for it yet. */
type VehicleFields = Partial<Omit<VehicleRecord, 'id'>>;

/**
 * The make, from either column. The server has both: a call created on the
 * dashboard fills vehicleName and one created by Urgently vehicleMake, and
 * the impound sheet once saved only vehicleName, so an impound whose
 * vehicleMake was set showed the old make after a correction.
 */
export const makeOf = (vehicle: VehicleFields | null | undefined) =>
  vehicle?.vehicleMake || vehicle?.vehicleName || '';

/** The sheet's starting values for a vehicle, blanks as ''. */
export const vehicleValuesOf = (
  vehicle: VehicleFields | null | undefined
): VehicleValues => ({
  vin: vehicle?.vin ?? '',
  make: makeOf(vehicle),
  model: vehicle?.vehicleModel ?? '',
  year: vehicle?.vehicleYear ? String(vehicle.vehicleYear) : '',
  color: vehicle?.vehicleAttributes?.color ?? '',
  odometer:
    vehicle?.odometer === null || vehicle?.odometer === undefined
      ? ''
      : String(vehicle.odometer),
  licensePlate: vehicle?.licensePlate ?? '',
});

/** Whether the vehicle has nothing on it yet: the call's Add vehicle tile. */
export const isBlankVehicle = (vehicle: VehicleFields | null | undefined) =>
  Object.values(vehicleValuesOf(vehicle)).every((value) => value === '');

/**
 * The sheet's values as PUT /calls/vehicle/:vehicleId takes them. Every
 * field is sent, a blank one as cleared, so emptying a box empties the
 * vehicle's field rather than leaving the old value: the route reads an
 * absent field as unchanged, a null as cleared and a year of 0 as none.
 * The make goes in both columns, so every screen reads the same one.
 */
export const toVehicleUpdate = (values: VehicleValues): UpdateVehicleDto => ({
  vin: values.vin,
  vehicleName: values.make || null,
  vehicleMake: values.make || null,
  vehicleModel: values.model || null,
  vehicleYear: values.year ? Number(values.year) : 0,
  odometer: values.odometer ? Number(values.odometer.replace(/,/g, '')) : null,
  licensePlate: values.licensePlate,
  // Merged into the saved attributes by the server, so the rest stay.
  vehicleAttributes: { color: values.color || null },
});

/**
 * Every field cleared: the vehicle sheet's Delete Vehicle. The server has no
 * route that deletes a call's vehicle, and should not: its photos, notes
 * and history hang off it. Clearing its details is what the update route
 * already allows anyone who may change it.
 */
export const CLEARED_VEHICLE: UpdateVehicleDto = toVehicleUpdate({
  vin: '',
  make: '',
  model: '',
  year: '',
  color: '',
  odometer: '',
  licensePlate: '',
});
